import React, { useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, PhoneCall, Check, X, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const AgentMarkdown = ({ text }) => (
  <div className="agent-md text-sm leading-relaxed [&_table]:w-full [&_table]:my-2 [&_table]:text-xs [&_th]:text-left [&_th]:bg-stone-100 [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_td]:border-t [&_td]:border-stone-200 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_strong]:font-semibold [&_a]:text-indigo-700 [&_a]:underline [&_a]:break-all [&_h3]:font-semibold [&_h3]:mt-2">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: (props) => <a {...props} target="_blank" rel="noreferrer" /> }}>{String(text || '')}</ReactMarkdown>
  </div>
);

const URL_RE = /(https?:\/\/[^\s)]+)/g;

export const Linkified = ({ text }) => (
  <>
    {String(text || '').split(URL_RE).map((part, i) => (URL_RE.test(part)
      ? <a key={i} href={part} target="_blank" rel="noreferrer" className="text-indigo-700 underline break-all">{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>))}
  </>
);

export const DraftActionCard = ({ action, onChange }) => {
  const [busy, setBusy] = useState(false);
  const text = action.preview?.text || '';
  const isCall = action.tool === 'call_patient';
  const p = action.preview || {};

  const run = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/agent-chat/actions/${action.action_id}/${kind === 'send' ? 'execute' : 'discard'}`);
      onChange({ ...action, status: data.status, result: data.result });
      if (data.status === 'done') toast.success(isCall ? 'Calling now…' : 'WhatsApp sent');
      if (data.status === 'failed') toast.error(data.result?.error || 'Failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs" data-testid={`draft-action-${action.action_id}`}>
      <div className="flex items-center gap-1.5 font-semibold text-stone-900">
        {isCall ? <PhoneCall className="w-3.5 h-3.5 text-rose-600" /> : <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />}
        {isCall ? 'AI phone call' : 'WhatsApp'} · {p.patient_name} <span className="font-mono text-stone-500 font-normal">{p.mobile}</span>
        <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${action.status === 'done' ? 'bg-emerald-600 text-white' : action.status === 'pending' ? 'bg-amber-200 text-amber-900' : 'bg-stone-200 text-stone-700'}`}>{action.status === 'pending' ? 'Draft' : action.status}</span>
      </div>
      {isCall ? (
        <div className="mt-1.5 text-stone-700"><b>Goal:</b> {p.intent || '—'}</div>
      ) : (
        <>
          {p.text && action.status === 'pending'
            ? <Textarea value={text} readOnly rows={Math.min(8, Math.max(3, text.split('\n').length))} className="mt-1.5 bg-white text-xs" data-testid={`draft-text-${action.action_id}`} />
            : p.text ? <div className="mt-1.5 whitespace-pre-wrap bg-white border border-stone-200 rounded p-2"><Linkified text={p.text} /></div> : null}
          {!p.session_open && <div className="mt-1 text-[10px] text-amber-800 font-medium">24h WhatsApp window is closed — the text and links above will NOT be delivered. Tapping Send sends the approved template ({p.template_id || 'independence_2026'}) to reopen the chat; send the video/PDF after the patient replies.</div>}
        </>
      )}
      {action.status === 'pending' && (
        <div className="mt-2 flex gap-1.5">
          <Button size="sm" onClick={() => run('send')} disabled={busy} className={`h-7 text-xs text-white ${isCall ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} data-testid={`draft-send-${action.action_id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}{isCall ? 'Call now' : 'Send'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => run('discard')} disabled={busy} className="h-7 text-xs" data-testid={`draft-discard-${action.action_id}`}><X className="w-3.5 h-3.5 mr-1" />Discard</Button>
        </div>
      )}
      {action.status === 'failed' && <div className="mt-1 text-red-700">{action.result?.error || 'Failed'}</div>}
    </div>
  );
};
